import React from "react";
import { Marker, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import { bestWildcadMatch, matchPerimeter } from "../../lib/dedupe";
import FireNameLabel from "./FireNameLabel";

// DNR fires render as emerald-green flames — same insignia family as WildCAD
// incidents (orange flames) but clearly a different agency/color.
// Green = currently active, gray = controlled/out.
const ACTIVE_FILL = "#10B981";
const ACTIVE_INNER = "#A7F3D0";
const INACTIVE_FILL = "#6B7280";
const INACTIVE_INNER = "#D1D5DB";

const _iconCache = {};
function dnrFlameIcon(active, size) {
  const key = `${active}-${size}`;
  if (!_iconCache[key]) {
    const fill = active ? ACTIVE_FILL : INACTIVE_FILL;
    const inner = active ? ACTIVE_INNER : INACTIVE_INNER;
    const opacity = active ? 1 : 0.65;
    _iconCache[key] = L.divIcon({
      className: "flame-marker",
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.65)); opacity:${opacity};">
        <path d="M12 1.5c.6 3.2-.9 5-2.5 6.8C7.7 10.3 6 12.2 6 15a6 6 0 0 0 12 0c0-2.2-.9-3.9-2-5.5-.4 1-.9 1.7-1.8 2.3.3-3.6-1-8-2.2-10.3z" fill="${fill}" stroke="#0A0D0C" stroke-width="1.4" stroke-linejoin="round"/>
        <path d="M12 20.4a3 3 0 0 1-3-3c0-1.4.8-2.3 1.6-3.2.5.7 1 1.1 1.6 1.4-.1-1.1.2-2.4.8-3.4 1.1 1.4 2 2.8 2 5.2a3 3 0 0 1-3 3z" fill="${inner}" opacity="0.92"/>
      </svg>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2 + 2],
    });
  }
  return _iconCache[key];
}

function acresSize(acres) {
  if (acres == null) return 26;
  const a = Number(acres) || 0;
  if (a >= 1000) return 34;
  if (a >= 100) return 28;
  if (a >= 10) return 24;
  if (a >= 1) return 20;
  return 18;
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DnrFiresLayer({ fires = [], activeId = null, onMarkerHover, wildcadIncidents = [], perimeters = [] }) {
  return fires.map((f) => {
    const active = !!f.is_active;
    const hovered = f.id === activeId;
    const size = acresSize(f.acres);
    const wcMatch = bestWildcadMatch(wildcadIncidents, f);
    return (
      <Marker
        key={`dnr-${f.id}`}
        pane="fire-icons"
        position={[f.latitude, f.longitude]}
        icon={dnrFlameIcon(active, hovered ? Math.round(size * 1.75) : size)}
        zIndexOffset={hovered ? 1200 : 0}
        eventHandlers={{
          mouseover: () => onMarkerHover && onMarkerHover(f.id),
          mouseout: () => onMarkerHover && onMarkerHover(null),
          click: () => onMarkerHover && onMarkerHover(null),
        }}
      >
        <Popup>
          <div className="min-w-[220px]" data-testid={`dnr-popup-${f.id}`}>
            <div
              className="text-[10px] font-bold uppercase tracking-wider mb-1"
              style={{ color: active ? ACTIVE_FILL : INACTIVE_FILL }}
            >
              WA DNR · {active ? "Active" : "Controlled / Out"}
            </div>
            <div className="text-[14px] font-bold text-tac-text mb-0.5">
              {f.name}
            </div>
            <div className="text-[12px] text-tac-dim font-mono-tac tabular space-y-0.5">
              {f.county && <div>{f.county} County</div>}
              <div>
                {f.acres != null ? `${f.acres.toFixed(2)} ac` : "— ac"}
                {(() => {
                  const pm = matchPerimeter(f, perimeters);
                  return pm?.acres != null &&
                    (f.acres == null || Math.round(pm.acres) !== Math.round(f.acres))
                    ? " official"
                    : "";
                })()}
                {f.elevation_ft != null && ` · ${f.elevation_ft} ft`}
              </div>
              {(() => {
                const pm = matchPerimeter(f, perimeters);
                return pm?.acres != null &&
                  (f.acres == null || Math.round(pm.acres) !== Math.round(f.acres)) ? (
                  <div>{Math.round(pm.acres).toLocaleString()} ac mapped perimeter</div>
                ) : null;
              })()}
              {f.cause && <div>Cause: {f.cause}</div>}
              {f.discovered_at && (
                <div>Discovered: {fmtDate(f.discovered_at)}</div>
              )}
              {f.controlled_at && (
                <div>Controlled: {fmtDate(f.controlled_at)}</div>
              )}
              {f.out_at && <div>Out: {fmtDate(f.out_at)}</div>}
            </div>
            {wcMatch && (
              <Link
                to={`/incident/${wcMatch.id}`}
                data-testid={`dnr-popup-details-${f.id}`}
                className="inline-block mt-2 text-[11px] font-bold uppercase tracking-wider hover:underline"
                style={{ color: active ? ACTIVE_FILL : INACTIVE_FILL }}
              >
                View details →
              </Link>
            )}
            <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
              Source: WA DNR Wildland Fire Mgmt Division
            </div>
          </div>
        </Popup>
        <FireNameLabel name={f.name} acres={f.acres} />
      </Marker>
    );
  });
}
